import type {Ref} from 'vue'
import {
    B2B_AUTH_EVENT,
    CHANGE_LOCALE_EVENT,
    CHANGE_PRICE_EVENT,
    EventBus,
    FILTER_EVENT,
    RESET,
    RESET_BOOKING_DATA,
    SEARCH_EVENT,
} from '@/utils/event-bus'
import {appInstance} from '@/utils/app-accessor'
// @ts-ignore
import {axiosInstance} from '@/utils/axios-accessor'
import {isEqual} from '@/utils/helpers'
import {
    filters as filtersBlank,
    searchRequest as searchRequestBlank,
    searchResponse as searchResponseBlank,
} from '~/utils/insurance/insurance-blank-states'
import {insuranceStore, persistentStore} from '@/composables/store-accessor'
import {useProductRuntimeStore} from '~/composables/product-runtime-store'

const runtimeFiltersBlank = () => ({})

export const useInsuranceRuntimeStore = defineStore('insuranceRuntime', () => {
    const productRuntimeStore = useProductRuntimeStore()
    const {
        offers,
        filterActiveCount,
        searchActiveCount,
        SET_OFFERS,
        START_SEARCH,
        STOP_SEARCH,
        START_FILTER,
        STOP_FILTER,
        filterActive,
        searchActive,
    } = productRuntimeStore

    const searchCTS: Ref = ref(null)
    const location: Ref = ref({})
    const runtimeFilters: Ref = ref(runtimeFiltersBlank())

    function _newSearch(searchRequest: any) {
        stopSearch()
        SET_OFFERS([])
        SET_RUNTIME_FILTERS(runtimeFiltersBlank())
        insuranceStore.SET_SEARCH_RESPONSE(searchResponseBlank())
        insuranceStore.NEW_SEARCH(searchRequest)
    }

    function _setSearchResponse(searchResponse: any) {
        if (insuranceStore.searchResponse.products.length === searchResponse.products.length) return
        insuranceStore.SET_FILTER({key: 'price', value: searchResponse.filters.price})
        insuranceStore.SET_SEARCH_RESPONSE(searchResponse)
        const {products} = searchResponse
        appInstance.$localForage.setItem('insurance', products)
        load(products)
    }

    async function _loadCountry(point: any, id: any) {
        try {
            const {
                countries: [country],
            } = await appInstance.$api.locations.get({id})
            return {[point]: country}
        } catch (e) {
            return {[point]: {}}
        }
    }

    function SET_RUNTIME_FILTERS(filters: any) {
        runtimeFilters.value = filters
    }

    function SET_SEARCH_CTS(cancelTokenSource: any) {
        searchCTS.value = cancelTokenSource
    }

    async function loadLocation(id: any) {
        location.value = ((await _loadCountry('location', id)) as any).location
    }

    function SET_DESTINATION_COUNTRY(country: any) {
        location.value = country
    }

    function clientInit() {
        EventBus.$on(RESET, reset)
        EventBus.$on(RESET_BOOKING_DATA, reset)
        EventBus.$on(B2B_AUTH_EVENT, reset)
        EventBus.$on(CHANGE_LOCALE_EVENT, reload)
        EventBus.$on(CHANGE_PRICE_EVENT, changePrice)
        appInstance.$workers.insuranceWorker.onmessage = ({data}: any) => {
            if (data === 'load') {
                if (
                    isEqual(
                        {...filtersBlank(), price: insuranceStore.searchResponse.filters.price},
                        insuranceStore.filters
                    )
                ) {
                    sort()
                } else {
                    filter()
                }
                return
            } else if (data === 'refresh') {
                return
            }
            STOP_FILTER()
            if (filterActive.value) return
            SET_OFFERS(data)
            EventBus.$emit(FILTER_EVENT)
        }
    }

    async function changePrice({offerKey, prepareBookResponse}: any) {
        insuranceStore.REFRESH_BASKET_PRICE({offerKey, prepareBookResponse})
        persistentStore.REFRESH_CONDITIONS({offerKey, prepareBookResponse})
    }

    function reset() {
        if (searchCTS.value) searchCTS.value.cancel()
        _newSearch(null)
        insuranceStore.RESET()
    }

    async function reload() {
        if (location.value.id) await loadLocation(location.value.id)
    }

    async function search(rq: any) {
        START_SEARCH()
        _newSearch(rq)
        EventBus.$emit(SEARCH_EVENT)
        try {
            // @ts-ignore
            const cancelTokenSource = axiosInstance.CancelToken.source()
            SET_SEARCH_CTS(cancelTokenSource)
            const searchResponse = await appInstance.$api.searchInsurance.get(rq, cancelTokenSource.token)
            _setSearchResponse(searchResponse)
            insuranceStore.SET_SEARCH_RESPONSE(searchResponse)
            // eslint-disable-next-line no-empty
        } catch (e) {
        } finally {
            STOP_SEARCH()
        }
    }

    function newSearch() {
        _newSearch(searchRequestBlank())
    }

    function stopSearch() {
        if (searchCTS.value) searchCTS.value.cancel()
    }

    function load(offers = []) {
        appInstance.$workers.insuranceWorker.postMessage({action: 'load', offers})
    }

    function sort() {
        START_FILTER()
        appInstance.$workers.insuranceWorker.postMessage({
            action: 'filter',
            filters: {...clone(insuranceStore.filters), ...runtimeFilters.value},
            sortKey: insuranceStore.sortFnName,
        })
    }

    function filter() {
        START_FILTER()
        appInstance.$workers.insuranceWorker.postMessage({
            action: 'filter',
            filters: {...clone(insuranceStore.filters), ...runtimeFilters.value},
            sortKey: insuranceStore.sortFnName,
        })
    }

    const touristsTotal = computed(() => {
        return (searchRequest: any) => searchRequest.adults + searchRequest.childrenAges.length
    })

    const searchPageLink = computed(() => {
        return (searchRequest: any) => {
            const {...query} = searchRequest
            return {name: 'insuranceSearch', query}
        }
    })

    return {
        ...productRuntimeStore,
        searchActiveCount,
        searchCTS,
        location,
        offers,
        filterActiveCount,
        runtimeFilters,
        START_FILTER,
        STOP_FILTER,
        SET_RUNTIME_FILTERS,
        SET_OFFERS,
        START_SEARCH,
        STOP_SEARCH,
        SET_SEARCH_CTS,
        loadLocation,
        SET_DESTINATION_COUNTRY,
        clientInit,
        changePrice,
        reset,
        reload,
        search,
        newSearch,
        stopSearch,
        load,
        sort,
        filter,
        searchActive,
        touristsTotal,
        searchPageLink,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useInsuranceRuntimeStore, import.meta.hot))
